import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { 
  AgentLedgerService, 
  AgentLedgerDto, 
  AgentLedgerFilterDto, 
  RecordAgentPaymentDto,
  CancelAgentLedgerDto,
  AgentLedgerPeriodSummaryDto,
  InvoicePaymentAllocationDto
} from '../../../services/agent-ledger.service';
import { AgentInvoiceService, AgentInvoiceDto } from '../../../services/agent-invoice.service';
import { ExternalDataService, Agent } from '../../../services/externalDataService.service';
import { PaginationComponent } from '../../shared/pagination/pagination.component';

@Component({
  selector: 'app-agent-ledger-list',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, PaginationComponent],
  templateUrl: './agent-ledger-list.component.html',
  styleUrl: './agent-ledger-list.component.css'
})
export class AgentLedgerListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // State properties
  ledgerEntries: AgentLedgerDto[] = [];
  agents: Agent[] = [];
  agentSummary: AgentLedgerPeriodSummaryDto | null = null;
  loading = false;
  errorMessage = '';
  successMessage = '';

  // Pending Invoices Allocation State
  pendingInvoices: AgentInvoiceDto[] = [];
  loadingPendingInvoices = false;
  invoiceAllocationsMap: { [invoiceId: string]: number } = {};
  showAllPendingInvoices = false;

  get displayedPendingInvoices(): AgentInvoiceDto[] {
    if (this.showAllPendingInvoices) {
      return this.pendingInvoices;
    }
    return this.pendingInvoices.slice(0, 5);
  }

  toggleShowAllPendingInvoices(): void {
    this.showAllPendingInvoices = !this.showAllPendingInvoices;
  }

  formatInvoiceId(id: string): string {
    if (!id) return '';
    return id.length > 14 ? `${id.substring(0, 13)}...` : id;
  }

  // Pagination properties
  pageIndex = 0;
  pageSize = 10;
  totalCount = 0;

  // Filter Form
  filterForm!: FormGroup;

  // Entry Modal & Form
  showEntryModal = false;
  entryForm!: FormGroup;
  submittingEntry = false;

  // Cancellation Modal & State
  showCancelModal = false;
  selectedEntryToCancel: AgentLedgerDto | null = null;
  cancelReason = '';
  cancellingEntry = false;

  // Date restriction properties
  maxPastDays: number = 30;
  minTransactionDate: string = '';
  maxTransactionDate: string = new Date().toISOString().split('T')[0];

  // Constants / Lists
  readonly entryTypes = [
    { value: '', label: 'All Entry Types' },
    { value: 'Invoice', label: 'Invoice' },
    { value: 'Payment', label: 'Payment' },
    { value: 'Adjustment', label: 'Adjustment' }
  ];

  readonly transactionTypes = [
    { value: 'All', label: 'All Transactions' },
    { value: 'Debit', label: 'Debits Only' },
    { value: 'Credit', label: 'Credits Only' }
  ];

  Math = Math;

  constructor(
    private fb: FormBuilder,
    private agentLedgerService: AgentLedgerService,
    private agentInvoiceService: AgentInvoiceService,
    private externalDataService: ExternalDataService
  ) {
    this.initForms();
  }

  ngOnInit(): void {
    this.loadAgents();
    this.loadGlobalSettings();
    this.loadLedgerEntries();
  }

  loadGlobalSettings(): void {
    this.externalDataService
      .getGlobalSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          if (settings && settings.maxPastDaysForLedgerEntry !== undefined && settings.maxPastDaysForLedgerEntry !== null) {
            this.maxPastDays = settings.maxPastDaysForLedgerEntry;
          }
          this.updateMinTransactionDate();
        },
        error: (err) => {
          console.error('Error loading global settings for ledger date limits:', err);
          this.updateMinTransactionDate();
        }
      });
  }

  updateMinTransactionDate(): void {
    if (this.maxPastDays > 0) {
      const minDate = new Date();
      minDate.setDate(minDate.getDate() - this.maxPastDays);
      this.minTransactionDate = minDate.toISOString().split('T')[0];
    } else {
      this.minTransactionDate = '';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForms(): void {
    this.filterForm = this.fb.group({
      agentId: [''],
      agentName: [''],
      fromTransactionDate: [''],
      toTransactionDate: [''],
      entryType: [''],
      transactionType: ['All']
    });

    this.entryForm = this.fb.group({
      agentId: ['', Validators.required],
      creditAmount: [null, [Validators.required, Validators.min(0.01)]],
      description: [''],
      transactionDate: [new Date().toISOString().split('T')[0], Validators.required],
      paymentMode: ['Bank Transfer'],
      referenceId: [''],
      referenceType: ['']
    });
  }

  get currentPage(): number {
    return this.pageIndex + 1;
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize) || 1;
  }
  isPettyCashMode(): boolean {
    return this.entryForm.get('paymentMode')?.value === 'Petty Cash';
  }

  loadAgents(): void {
    this.externalDataService
      .getAllAgents(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = [...agents].sort((a, b) => a.name.localeCompare(b.name));
        },
        error: (error) => {
          console.error('Error loading agents:', error);
        }
      });
  }

  loadLedgerEntries(): void {
    this.loading = true;
    this.errorMessage = '';

    const filters = this.filterForm.value;
    const requestFilters: AgentLedgerFilterDto = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      agentId: filters.agentId || undefined,
      agentName: filters.agentName?.trim() || undefined,
      fromTransactionDate: filters.fromTransactionDate || undefined,
      toTransactionDate: filters.toTransactionDate || undefined,
      entryType: filters.entryType || undefined,
      transactionType: filters.transactionType || 'All'
    };

    this.agentLedgerService
      .getLedgerEntries(requestFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.ledgerEntries = response.items || [];
          this.totalCount = response.totalCount || 0;
          this.agentSummary = response.summary || null;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading ledger entries:', error);
          this.errorMessage = 'Failed to load agent ledger entries. Please check connection and try again.';
          this.loading = false;
        }
      });
  }

  applyFilters(): void {
    this.pageIndex = 0;
    this.loadLedgerEntries();
  }

  clearFilters(): void {
    this.agentSummary = null;
    this.filterForm.reset({
      agentId: '',
      agentName: '',
      fromTransactionDate: '',
      toTransactionDate: '',
      entryType: '',
      transactionType: 'All'
    });
    this.pageIndex = 0;
    this.loadLedgerEntries();
  }

  hasActiveFilters(): boolean {
    const v = this.filterForm.value;
    return !!(v.agentId || v.agentName?.trim() || v.fromTransactionDate || v.toTransactionDate || v.entryType || v.transactionType !== 'All');
  }

  onPageChange(page: number): void {
    this.pageIndex = page - 1;
    this.loadLedgerEntries();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 0;
    this.loadLedgerEntries();
  }

  // Modal actions
  // Modal actions
  openAddEntryModal(): void {
    this.showEntryModal = true;
    this.successMessage = '';
    this.errorMessage = '';
    this.pendingInvoices = [];
    this.invoiceAllocationsMap = {};
    this.loadingPendingInvoices = false;
    this.showAllPendingInvoices = false;
    this.entryForm.reset({
      agentId: '',
      creditAmount: null,
      description: '',
      transactionDate: new Date().toISOString().split('T')[0],
      paymentMode: 'Bank Transfer',
      staffId: '',
      referenceId: '',
      referenceType: ''
    });
  }

  onModalAgentChange(agentId: string): void {
    this.pendingInvoices = [];
    this.invoiceAllocationsMap = {};
    this.showAllPendingInvoices = false;
    if (!agentId) return;

    this.loadingPendingInvoices = true;
    this.agentInvoiceService
      .getPendingInvoices(agentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (invoices) => {
          const list = invoices || [];
          this.pendingInvoices = [...list].sort((a, b) =>
            new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime()
          );
          this.loadingPendingInvoices = false;
        },
        error: (err) => {
          console.error('Error loading pending invoices:', err);
          this.loadingPendingInvoices = false;
        }
      });
  }

  getInvoiceBalanceDue(invoice: AgentInvoiceDto): number {
    if (invoice.balanceDue !== undefined && invoice.balanceDue !== null) {
      return invoice.balanceDue;
    }
    return Math.max(0, invoice.totalAmount - (invoice.paidAmount || 0));
  }

  payFullInvoice(invoice: AgentInvoiceDto): void {
    const due = this.getInvoiceBalanceDue(invoice);
    this.invoiceAllocationsMap[invoice.invoiceId] = due;
    this.updateCreditAmountFromAllocations();
  }

  clearInvoiceAllocation(invoiceId: string): void {
    delete this.invoiceAllocationsMap[invoiceId];
    this.updateCreditAmountFromAllocations();
  }

  getTotalAllocatedAmount(): number {
    return Object.values(this.invoiceAllocationsMap)
      .reduce((sum, val) => sum + (Number(val) || 0), 0);
  }

  updateCreditAmountFromAllocations(): void {
    const total = this.getTotalAllocatedAmount();
    if (total > 0) {
      this.entryForm.patchValue({ creditAmount: Number(total.toFixed(2)) });
    }
  }

  autoAllocatePayment(): void {
    let credit = Number(this.entryForm.get('creditAmount')?.value) || 0;
    if (credit <= 0) return;

    this.invoiceAllocationsMap = {};
    for (const inv of this.pendingInvoices) {
      if (credit <= 0) break;
      const due = this.getInvoiceBalanceDue(inv);
      const allocated = Math.min(credit, due);
      this.invoiceAllocationsMap[inv.invoiceId] = Number(allocated.toFixed(2));
      credit -= allocated;
    }
  }

  closeAddEntryModal(): void {
    this.showEntryModal = false;
  }

  submitEntry(): void {
    if (this.entryForm.invalid) {
      this.entryForm.markAllAsTouched();
      return;
    }

    const formVal = this.entryForm.value;
    if (this.isPettyCashMode() && !formVal.referenceId?.trim()) {
      this.errorMessage = 'Reference ID is required when payment mode is Petty Cash.';
      return;
    }

    if (this.minTransactionDate && formVal.transactionDate < this.minTransactionDate) {
      this.errorMessage = `Transaction date cannot be more than ${this.maxPastDays} days in the past (earliest allowed: ${this.minTransactionDate}).`;
      return;
    }

    this.submittingEntry = true;
    this.errorMessage = '';
    this.successMessage = '';

    const invoiceAllocationsList: InvoicePaymentAllocationDto[] = Object.entries(this.invoiceAllocationsMap)
      .filter(([_, amount]) => Number(amount) > 0)
      .map(([invoiceId, amount]) => ({ invoiceId, amount: Number(amount) }));

    const dto: RecordAgentPaymentDto = {
      agentId: formVal.agentId,
      creditAmount: formVal.creditAmount,
      description: formVal.description?.trim() || undefined,
      transactionDate: new Date(formVal.transactionDate).toISOString(),
      referenceId: formVal.referenceId?.trim() || undefined,
      referenceType: formVal.referenceType?.trim() || undefined,
      paymentMode: formVal.paymentMode || undefined,
      invoiceAllocations: invoiceAllocationsList.length > 0 ? invoiceAllocationsList : undefined
    };

    this.agentLedgerService
      .recordPayment(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.submittingEntry = false;
          this.showEntryModal = false;
          this.successMessage = 'Agent payment recorded successfully!';
          this.pageIndex = 0;
          this.loadLedgerEntries();
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            if (this.successMessage === 'Agent payment recorded successfully!') {
              this.successMessage = '';
            }
          }, 5000);
        },
        error: (error) => {
          console.error('Error recording agent payment:', error);
          this.errorMessage = error?.error?.message || 'Failed to record agent payment. Please check values and try again.';
          this.submittingEntry = false;
        }
      });
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatCurrency(amount?: number | null): string {
    if (amount === undefined || amount === null) return '—';
    return `₹${amount.toFixed(2)}`;
  }

  getEntryTypeLabel(type: string): string {
    const match = this.entryTypes.find(t => t.value === type);
    return match ? match.label : type;
  }

  // Cancellation Actions
  openCancelModal(entry: AgentLedgerDto): void {
    this.selectedEntryToCancel = entry;
    this.cancelReason = '';
    this.showCancelModal = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
    this.selectedEntryToCancel = null;
    this.cancelReason = '';
  }

  confirmCancelEntry(): void {
    if (!this.selectedEntryToCancel) return;

    this.cancellingEntry = true;
    this.errorMessage = '';
    this.successMessage = '';

    const dto: CancelAgentLedgerDto = {
      agentId: this.selectedEntryToCancel.agentId,
      ledgerEntryId: this.selectedEntryToCancel.id,
      reason: this.cancelReason.trim() || undefined
    };

    this.agentLedgerService
      .cancelLedgerEntry(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.cancellingEntry = false;
          this.showCancelModal = false;
          this.selectedEntryToCancel = null;
          this.cancelReason = '';
          this.successMessage = 'Ledger entry cancelled successfully. Reverse transaction has been posted.';
          this.loadLedgerEntries();

          setTimeout(() => {
            if (this.successMessage.includes('cancelled successfully')) {
              this.successMessage = '';
            }
          }, 5000);
        },
        error: (error) => {
          console.error('Error cancelling ledger entry:', error);
          this.errorMessage =
            error?.error?.error ||
            error?.error?.message ||
            'Failed to cancel ledger entry. Please try again.';
          this.cancellingEntry = false;
        }
      });
  }

  canCancelEntry(entry: AgentLedgerDto): boolean {
    return !entry.isCancelled;
  }
}
